import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@uidotdev/usehooks";
import { useQuery } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system";
import LoadingWheel from "../shared/LoadingWheel";
import { UKVDResponse } from "../../models/UKVDResponse";
import apiConfig from "../../utils/apiConfig";


interface MakeComboboxProps {
  onMakeSelect: (make: UKVDResponse) => void;
  currentValue?: string;
  label?: string;
}

export function MakeCombobox({ onMakeSelect, currentValue, label }: MakeComboboxProps) {
  const { t } = useTranslation("stock");
  const { get } = useApiHelper(apiConfig("MakeCombobox"));
  const [selectedMake, setSelectedMake] = useState<UKVDResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["make", "lookup"],
    queryFn: () =>
      get<DefaultResponseWithData<UKVDResponse[]>>(
        `/utilities/lookup/makes`
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true,
  });

  const makes = useMemo(() => {
    const allMakes = data?.data ?? [];

    if (!debouncedSearch) return allMakes;

    return allMakes.filter(make =>
      make.make.toLowerCase().includes(debouncedSearch.toLowerCase())
    )
  }, [data, debouncedSearch]);


  useEffect(() => {
    if (isLoading || currentValue === undefined) return;

    if (currentValue === "") {
      setSelectedMake(null);
      setSearchTerm("");
      return;
    }

    const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, " ");
    const searchValue = normalize(currentValue);
    const foundMake = data?.data.find(make => normalize(make.make) === searchValue);

    if (foundMake && foundMake !== selectedMake) {
      setSelectedMake(foundMake);
      setSearchTerm(foundMake.make);
    } else {
      // Clear selection so the input shows the programmatic text
      setSelectedMake(null);
      setSearchTerm(currentValue);
    }

  }, [currentValue, data, isLoading]);


  return (
    <div>
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        by="make"
        value={selectedMake ?? null}
        onChange={(make: UKVDResponse) => {
          setSelectedMake(make);
          onMakeSelect(make);
        }}
        key={currentValue || ""}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400"
            onChange={(event) => setSearchTerm(event.target.value)}
            displayValue={(make: UKVDResponse) => make?.make || searchTerm}
            placeholder={t("Search makes...")}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {(isLoading || isFetching) ? (
              <div className="flex items-center justify-center h-5 w-5">
                <LoadingWheel />
              </div>
            ) : (
              <ChevronUpDownIcon
                className="size-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-b-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {makes && makes.length > 0 ? (
              makes.slice(0, 100).map((make) => (
                <ComboboxOption
                  key={make.make}
                  value={make}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {make.make}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                            }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                {t("Nothing found.")}
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox >
    </div >
  );
}
